import { useEffect, useMemo, useState } from "react";
import api, { brl } from "../lib/api";
import { openPdf } from "../lib/fiscal";
import { toast } from "sonner";
import {
  Plus, X, FileText, Receipt, MessageCircle, ShieldCheck, Pencil, Bot,
} from "lucide-react";
import { BRAND_MODELS, checklistFor, CHECKLIST_STATUS, STATUS_LABEL } from "../lib/phoneCatalog";
import { computeOverdue, buildStorageChargeMsg } from "../lib/osStorage";
import OsPhotoUploader from "../components/OsPhotoUploader";
import ImeiHistoryInline from "../components/ImeiHistoryInline";

const TRACK_BASE = (typeof window !== "undefined")
  ? `${window.location.origin}/track/`
  : "/track/";

const EMPTY = {
  customer_name: "",
  customer_phone: "",
  brand: "",
  device: "",
  imei: "",
  problem: "",
  diagnosis: "",
  price: 0,
  status: "recebido",
  notes: "",
  checklist: {}, // {key: {status, note}}
  storage_start: null,
  photos_entry: [],
  photos_exit: [],
};

const STATUS = [
  { k: "pre_agendada", label: "Pré-agendada (WhatsApp)", c: "bg-green-100 text-green-700" },
  { k: "recebido", label: "Recebido", c: "bg-slate-200 text-slate-700" },
  { k: "em_reparo", label: "Em Reparo", c: "bg-amber-100 text-amber-700" },
  { k: "pronto", label: "Pronto", c: "bg-emerald-100 text-emerald-700" },
  { k: "entregue", label: "Entregue", c: "bg-blue-100 text-blue-700" },
  { k: "cancelado", label: "Cancelado", c: "bg-red-100 text-red-700" },
];

// -------- Mensagens WhatsApp --------
const trackUrl = (os) => `${TRACK_BASE}${os.id || ""}`;

const buildOpeningMsg = (os) => {
  const brand = os.brand || "";
  const dev = os.device || "";
  const lines = [
    `*UP CEL Quilombo — Ordem de Serviço #${(os.id || "").slice(0, 8).toUpperCase()}*`,
    "",
    `👤 Olá ${os.customer_name || "cliente"}!`,
    "",
    `Recebemos seu *${brand ? brand + " " : ""}${dev}* para o serviço:`,
    `🛠️ ${os.problem || "—"}`,
    os.price > 0 ? `💰 Valor: ${brl(os.price)}` : "",
    "",
    `📲 *Acompanhe sua OS em tempo real:*`,
    trackUrl(os),
    "",
    `Na página acima você vê:`,
    `• Status atualizado (Recebido → Em Reparo → Pronto → Entregue)`,
    `• Fotos de entrada e saída do aparelho`,
    `• Checklist completo do recebimento`,
    "",
    "*⚠️ Taxa de armazenamento:* após avisado que está pronto, o cliente tem *3 dias* para retirada. Depois: *R$ 1,00/dia*.",
    "",
    "Qualquer dúvida, é só chamar! 💙",
    "— UP CEL Quilombo",
  ].filter(Boolean).join("\n");
  return lines;
};

const buildConclusionMsg = (os) => {
  const brand = os.brand || "";
  const dev = os.device || "";
  const lines = [
    `*UP CEL Quilombo — OS #${(os.id || "").slice(0, 8).toUpperCase()} PRONTA! ✅*`,
    "",
    `👤 Olá ${os.customer_name || "cliente"}!`,
    "",
    `Seu *${brand ? brand + " " : ""}${dev}* já está pronto para retirada.`,
    os.diagnosis ? `🔧 Serviço realizado: ${os.diagnosis}` : "",
    os.price > 0 ? `💰 Valor a pagar: ${brl(os.price)}` : "",
    "",
    `📲 *Veja fotos do antes/depois e todos os detalhes:*`,
    trackUrl(os),
    "",
    `📌 Você tem *3 dias* para retirar sem custo adicional.`,
    `Após esse prazo: *R$ 1,00/dia* de armazenamento.`,
    "",
    `🛡️ *Garantia: 3 meses* contra defeitos de fábrica (peças e serviço).`,
    `Perde a garantia se: aberto por terceiros, mau uso, líquidos, quedas ou lacre violado.`,
    "",
    "Muito obrigado pela confiança! 💙",
    "— UP CEL Quilombo",
  ].filter(Boolean).join("\n");
  return lines;
};

const whatsUrl = (phone, text) => {
  const clean = String(phone || "").replace(/\D/g, "");
  const num = clean.length === 11 || clean.length === 10 ? `55${clean}` : clean;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

// ---------- Checklist Editor ----------
function ChecklistEditor({ brand, model, value, onChange }) {
  const items = useMemo(() => checklistFor(brand, model), [brand, model]);
  const set = (key, patch) =>
    onChange({ ...value, [key]: { ...(value[key] || {}), ...patch } });

  if (!brand || !model) {
    return (
      <div className="text-xs text-slate-500 border border-dashed border-slate-300 p-4 text-center rounded-sm">
        Selecione a <b>marca</b> e o <b>modelo</b> acima para montar a checklist inteligente.
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] text-slate-500 mb-2">
        Checklist automática para <b>{brand} {model}</b> — marque como cada item está.
      </div>
      {items.map((it) => {
        const cur = value[it.key] || {};
        return (
          <div key={it.key}
            className="border border-slate-200 rounded-sm p-2 flex flex-wrap gap-2 items-center bg-white">
            <div className="flex-1 min-w-[120px] text-xs font-medium">{it.label}</div>
            <div className="flex gap-1">
              {CHECKLIST_STATUS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => set(it.key, { status: s.value })}
                  data-testid={`os-check-${it.key}-${s.value}`}
                  className={`text-[10px] px-2 py-1 rounded-sm border font-semibold uppercase tracking-wider ${
                    cur.status === s.value
                      ? s.cls
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={cur.note || ""}
              onChange={(e) => set(it.key, { note: e.target.value })}
              placeholder="Observação (opcional)"
              className="basis-full md:basis-auto md:flex-1 md:min-w-[160px] px-2 py-1 border border-slate-200 rounded-sm text-xs"
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------- Main Component ----------
function AiOsBanner({ count }) {
  if (!count) return null;
  return (
    <div className="bg-green-50 border border-green-300 rounded-sm px-4 py-3 flex items-center gap-3" data-testid="os-ai-banner">
      <Bot size={18} className="text-green-700" />
      <span className="text-sm text-green-900">
        <b>{count}</b> OS pré-agendada(s) pela IA do WhatsApp aguardando o aparelho. Quando o cliente chegar, mude o status para <b>Recebido</b>.
      </span>
    </div>
  );
}

export default function ServiceOrders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/service-orders").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const brands = Object.keys(BRAND_MODELS);
  const models = form.brand ? (BRAND_MODELS[form.brand] || []) : [];

  const save = async () => {
    if (!form.customer_name || !form.device) {
      toast.error("Preencha cliente e modelo");
      return;
    }
    try {
      const payload = { ...form };
      if (editId) {
        await api.patch(`/service-orders/${editId}`, payload);
        toast.success("OS atualizada!");
      } else {
        await api.post("/service-orders", payload);
        toast.success("OS criada!");
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const changeStatus = async (id, status) => {
    try {
      const patch = { status };
      if (status === "pronto") patch.storage_start = new Date().toISOString();
      await api.patch(`/service-orders/${id}`, patch);
      toast.success("Status atualizado");
      load();
    } catch { toast.error("Erro"); }
  };

  const editOs = (o) => {
    setForm({ ...EMPTY, ...o, checklist: o.checklist || {} });
    setEditId(o.id);
    setOpen(true);
  };

  const sendOpening = (o) => {
    if (!o.customer_phone) { toast.error("Cliente sem telefone cadastrado"); return; }
    window.open(whatsUrl(o.customer_phone, buildOpeningMsg(o)), "_blank");
  };
  const sendConclusion = (o) => {
    if (!o.customer_phone) { toast.error("Cliente sem telefone cadastrado"); return; }
    window.open(whatsUrl(o.customer_phone, buildConclusionMsg(o)), "_blank");
  };

  return (
    <div className="space-y-6" data-testid="service-orders-page">
      <header className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
            Manutenção e Reparos
          </div>
          <h1 className="font-display text-4xl font-bold">Ordens de Serviço</h1>
        </div>
        <button
          onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}
          data-testid="os-new-btn"
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"
        >
          <Plus size={16} /> Nova OS
        </button>
      </header>

      {/* Kanban */}
      <AiOsBanner count={items.filter((i) => i.status === "pre_agendada").length} />
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {STATUS.map((st) => (
          <div key={st.k} className="bg-slate-100 rounded-sm p-3 min-h-[400px]">
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded-sm ${st.c}`}>
                {st.label}
              </span>
              <span className="text-xs text-slate-500">
                {items.filter((i) => i.status === st.k).length}
              </span>
            </div>
            <div className="space-y-2">
              {items.filter((i) => i.status === st.k).map((o) => {
                const overdueInfo = computeOverdue(o);
                return (
                <div key={o.id}
                  className={`bg-white p-3 rounded-sm border text-sm ${overdueInfo?.overdue ? "border-red-400 ring-1 ring-red-200" : o.status === "pre_agendada" ? "border-green-400 ring-1 ring-green-200" : "border-slate-200"}`}
                  data-testid={`os-card-${o.id}`}
                >
                  {overdueInfo?.overdue && (
                    <div
                      className="mb-2 -mx-3 -mt-3 px-3 py-1.5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-between rounded-t-sm"
                      data-testid={`os-overdue-badge-${o.id}`}
                    >
                      <span>ATRASADA — {overdueInfo.daysOverdue}d de taxa</span>
                      <span>{brl(overdueInfo.fee)}</span>
                    </div>
                  )}
                  {o.source === "whatsapp_ai" && (
                    <div className="mb-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-green-600 text-white px-1.5 py-0.5 rounded-sm"
                      data-testid={`os-ai-badge-${o.id}`}>
                      <Bot size={10} /> Aberta pela IA
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-1">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {o.brand ? `${o.brand} ` : ""}{o.device}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{o.customer_name}</div>
                    </div>
                    <button
                      onClick={() => editOs(o)}
                      data-testid={`os-edit-${o.id}`}
                      className="p-1 hover:bg-slate-100 rounded-sm"
                      title="Editar OS"
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                  <div className="text-xs text-slate-600 mt-2 line-clamp-2">{o.problem}</div>
                  {o.price > 0 && (
                    <div className="text-sm font-semibold text-[#2563EB] mt-2">{brl(o.price)}</div>
                  )}
                  <select
                    value={o.status}
                    onChange={(e) => changeStatus(o.id, e.target.value)}
                    data-testid={`os-status-${o.id}`}
                    className="mt-2 w-full text-xs border border-slate-300 rounded-sm px-2 py-1"
                  >
                    {STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>

                  {/* WhatsApp actions */}
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    <button
                      onClick={() => sendOpening(o)}
                      data-testid={`os-wa-opening-${o.id}`}
                      title="Enviar OS + aviso taxa"
                      className="flex items-center justify-center gap-1 text-[10px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-sm py-1 font-semibold"
                    >
                      <MessageCircle size={10} /> Abertura
                    </button>
                    {(o.status === "pronto" || o.status === "entregue") && (
                      <button
                        onClick={() => sendConclusion(o)}
                        data-testid={`os-wa-conclusion-${o.id}`}
                        title="Enviar aviso 'Pronto' + Garantia"
                        className="flex items-center justify-center gap-1 text-[10px] bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-sm py-1 font-semibold"
                      >
                        <ShieldCheck size={10} /> Pronto + Gar.
                      </button>
                    )}
                  </div>

                  {overdueInfo?.overdue && (
                    <button
                      onClick={() => {
                        const clean = String(o.customer_phone || "").replace(/\D/g, "");
                        const num = clean.length === 11 || clean.length === 10 ? `55${clean}` : clean;
                        if (!num) return toast.error("Cliente sem telefone cadastrado.");
                        const text = buildStorageChargeMsg(o, overdueInfo);
                        window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank");
                      }}
                      data-testid={`os-charge-storage-${o.id}`}
                      disabled={!o.customer_phone}
                      title={o.customer_phone ? `Cobrar taxa de ${brl(overdueInfo.fee)} pelo WhatsApp` : "Cliente sem telefone"}
                      className="w-full mt-2 flex items-center justify-center gap-1 text-[10px] bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-sm py-1.5 font-semibold"
                    >
                      <MessageCircle size={11} /> Cobrar taxa {brl(overdueInfo.fee)}
                    </button>
                  )}

                  {/* PDF actions */}
                  <div className="flex gap-1 mt-1">
                    <button
                      onClick={() => openPdf(`/fiscal/receipt/os/thermal/entry/${o.id}`, `os-abertura-${o.id.slice(0, 8)}.pdf`)}
                      title="Cupom Térmico 80mm — Abertura (com checklist e aviso de taxa)"
                      data-testid={`os-thermal-entry-${o.id}`}
                      className="flex-1 flex items-center justify-center gap-1 text-[10px] border border-slate-300 hover:bg-slate-100 rounded-sm py-1"
                    >
                      <FileText size={10} /> Abertura 80mm
                    </button>
                    <button
                      onClick={() => openPdf(`/fiscal/receipt/os/thermal/delivery/${o.id}`, `os-entrega-${o.id.slice(0, 8)}.pdf`)}
                      title="Cupom Térmico 80mm — Entrega (com termo de garantia 3 meses)"
                      data-testid={`os-thermal-delivery-${o.id}`}
                      className="flex-1 flex items-center justify-center gap-1 text-[10px] border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm py-1"
                    >
                      <Receipt size={10} /> Entrega 80mm
                    </button>
                  </div>
                  <div className="flex gap-1 mt-1">
                    <button
                      onClick={() => openPdf(`/fiscal/receipt/os/budget/${o.id}`, `orcamento-${o.id.slice(0, 8)}.pdf`)}
                      title="Orçamento A4 (formato completo)"
                      data-testid={`os-budget-${o.id}`}
                      className="flex-1 flex items-center justify-center gap-1 text-[9px] text-slate-500 hover:text-slate-700 rounded-sm py-0.5"
                    >
                      Orçamento A4
                    </button>
                    <button
                      onClick={() => openPdf(`/fiscal/receipt/os/delivery/${o.id}`, `contra-recibo-${o.id.slice(0, 8)}.pdf`)}
                      title="Contra-Recibo A4 (formato completo)"
                      data-testid={`os-delivery-a4-${o.id}`}
                      className="flex-1 flex items-center justify-center gap-1 text-[9px] text-slate-500 hover:text-slate-700 rounded-sm py-0.5"
                    >
                      Entrega A4
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-sm max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto"
            data-testid="os-modal"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl font-bold">
                {editId ? "Editar OS" : "Nova Ordem de Serviço"}
              </h2>
              <button onClick={() => { setOpen(false); setEditId(null); }}>
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Cliente *
                </label>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-customer_name-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Telefone (WhatsApp) *
                </label>
                <input
                  type="text"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="(49) 99999-9999"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-customer_phone-input"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  IMEI / Série
                </label>
                <input
                  type="text"
                  value={form.imei}
                  onChange={(e) => setForm({ ...form, imei: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-imei-input"
                  placeholder="Digite o IMEI para consultar histórico automaticamente"
                />
                <ImeiHistoryInline
                  imei={form.imei}
                  currentOsId={editId}
                  onFill={(data) => setForm({ ...form, ...data })}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Marca *
                </label>
                <select
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value, device: "" })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-brand-input"
                >
                  <option value="">Selecione…</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Modelo *
                </label>
                <input
                  list="model-list"
                  type="text"
                  value={form.device}
                  onChange={(e) => setForm({ ...form, device: e.target.value })}
                  placeholder="Digite ou escolha…"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-device-input"
                />
                <datalist id="model-list">
                  {models.map((m) => <option key={m} value={m} />)}
                </datalist>
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Defeito Relatado *
                </label>
                <textarea
                  rows={2}
                  value={form.problem}
                  onChange={(e) => setForm({ ...form, problem: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-problem-input"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Diagnóstico Técnico / Serviço realizado
                </label>
                <textarea
                  rows={2}
                  value={form.diagnosis}
                  onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-diagnosis-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Valor (R$)
                </label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-price-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                >
                  {STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Observações
                </label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  data-testid="os-notes-input"
                />
              </div>

              {/* Checklist inteligente */}
              <div className="col-span-2 border-t border-slate-100 pt-3 mt-1">
                <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
                  📋 Checklist do Aparelho (recebimento)
                </label>
                <ChecklistEditor
                  brand={form.brand}
                  model={form.device}
                  value={form.checklist || {}}
                  onChange={(cl) => setForm({ ...form, checklist: cl })}
                />
              </div>

              {/* Fotos */}
              <div className="col-span-2 border-t border-slate-100 pt-3 mt-1 space-y-4">
                <OsPhotoUploader
                  osId={editId}
                  kind="entry"
                  photos={form.photos_entry || []}
                  onChange={(list) => setForm({ ...form, photos_entry: list })}
                  label="📸 Fotos de ENTRADA (como o aparelho chegou)"
                />
                <OsPhotoUploader
                  osId={editId}
                  kind="exit"
                  photos={form.photos_exit || []}
                  onChange={(list) => setForm({ ...form, photos_exit: list })}
                  label="✅ Fotos de SAÍDA (após o reparo)"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => { setOpen(false); setEditId(null); }}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                data-testid="os-save-btn"
                className="bg-[#2563EB] text-white px-4 py-2 rounded-sm text-sm font-semibold"
              >
                Salvar OS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
